/**
 * Meta Pixel event tracking utilities with RODO/GDPR consent gating.
 *
 * The base Meta Pixel script in index.html fires an initial PageView on load.
 * This module wraps all *additional* event calls so they are only sent when
 * the user has granted marketing/analytics consent.
 */

const CONSENT_KEY = 'cookie-consent'

function isStaging() {
  if (typeof window === 'undefined') return false
  return window.location.hostname.includes('staging')
}

export function getEnvironment() {
  return isStaging() ? 'staging' : 'production'
}

export function hasConsent() {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(CONSENT_KEY) === 'granted'
}

export function grantConsent() {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(CONSENT_KEY, 'granted')
  if (window.fbq) {
    window.fbq('consent', 'grant')
  }
}

export function denyConsent() {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(CONSENT_KEY, 'denied')
  if (window.fbq) {
    window.fbq('consent', 'revoke')
  }
}

export function getConsentChoice() {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(CONSENT_KEY)
}

function buildParams(params = {}) {
  return {
    environment: getEnvironment(),
    ...params,
  }
}

/**
 * Fire a standard Meta Pixel event (e.g. 'AddToCart', 'Purchase').
 * No-op if the user has not granted consent or if fbq is unavailable.
 */
export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return
  if (!hasConsent()) return
  if (!window.fbq) return
  window.fbq('track', eventName, buildParams(params))
}

/**
 * Fire a custom Meta Pixel event (e.g. 'CompletePaymentResumed').
 * No-op if the user has not granted consent or if fbq is unavailable.
 */
export function trackCustomEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return
  if (!hasConsent()) return
  if (!window.fbq) return
  window.fbq('trackCustom', eventName, buildParams(params))
}

/**
 * Fire a PageView. Used by the route-change hook; gated by consent.
 */
export function trackPageView() {
  trackEvent('PageView')
}

/**
 * Build standard e-commerce parameters from a bundle item.
 */
export function buildItemParams(item, extra = {}) {
  return {
    content_ids: [item.slug],
    content_name: item.name,
    content_type: 'product',
    value: item.priceMonthly * (item.quantity || 1),
    currency: 'PLN',
    quantity: item.quantity || 1,
    ...extra,
  }
}

/**
 * Build parameters for a list of bundle items (cart, checkout, purchase).
 */
export function buildBundleParams(items, extra = {}) {
  const contentIds = items.map((item) => item.slug)
  const value = items.reduce((sum, item) => sum + item.priceMonthly * (item.quantity || 1), 0)
  const numItems = items.reduce((sum, item) => sum + (item.quantity || 1), 0)

  return {
    content_ids: contentIds,
    content_type: 'product',
    value,
    currency: 'PLN',
    num_items: numItems,
    ...extra,
  }
}

/**
 * Fire ViewContent for a subscription detail page.
 */
export function trackViewContent(subscription) {
  if (!subscription) return
  trackEvent('ViewContent', {
    content_ids: [subscription.id],
    content_name: subscription.title,
    content_type: 'product',
    value: subscription.priceMonthly,
    currency: 'PLN',
  })
}

/**
 * Fire AddToCart for a subscription tier.
 */
export function trackAddToCart(subscription, quantity = 1) {
  if (!subscription) return
  trackEvent('AddToCart', {
    content_ids: [subscription.id],
    content_name: subscription.title,
    content_type: 'product',
    value: subscription.priceMonthly * quantity,
    currency: 'PLN',
    quantity,
  })
}
